import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, AlertCircle, Save, Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { profitCalculatorService } from '../../services/profitCalculatorService';
import AdminHeader from '../admin/components/AdminHeader';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import IrrfBracketRow from './components/IrrfBracketRow';
import IrrfBracketModal from './components/IrrfBracketModal';

const TaxConfigurationSettings = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [taxSettings, setTaxSettings] = useState({
    defaultDasPercentual: '6.0',
    defaultProlaboreMensal: '5000.00',
    defaultInssPercentual: '11.0'
  });
  
  const [irrfBrackets, setIrrfBrackets] = useState([]);
  const [showBracketModal, setShowBracketModal] = useState(false);
  const [editingBracket, setEditingBracket] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (profile?.role !== 'admin') {
        navigate('/projeto');
      } else {
        loadData();
      }
    }
  }, [user, profile, authLoading, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settings, brackets] = await Promise.all([
        profitCalculatorService?.getTaxSettings(),
        profitCalculatorService?.getIrrfBrackets()
      ]);
      
      setTaxSettings({
        defaultDasPercentual: settings?.defaultDasPercentual?.toString() || '6.0',
        defaultProlaboreMensal: settings?.defaultProlaboreMensal?.toString() || '5000.00',
        defaultInssPercentual: settings?.defaultInssPercentual?.toString() || '11.0'
      });
      
      setIrrfBrackets(brackets || []);
      setError('');
    } catch (err) {
      setError('Erro ao carregar configurações: ' + err?.message);
      console.error('Erro ao carregar configurações:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      
      await profitCalculatorService?.updateTaxSettings({
        defaultDasPercentual: parseFloat(taxSettings?.defaultDasPercentual),
        defaultProlaboreMensal: parseFloat(taxSettings?.defaultProlaboreMensal),
        defaultInssPercentual: parseFloat(taxSettings?.defaultInssPercentual)
      });
      
      setSuccess('Configurações salvas com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar configurações: ' + err?.message);
      console.error('Erro ao salvar:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddBracket = () => {
    setEditingBracket(null);
    setShowBracketModal(true);
  };

  const handleEditBracket = (bracket) => {
    setEditingBracket(bracket);
    setShowBracketModal(true);
  };

  const handleDeleteBracket = async (id) => {
    if (!confirm('Tem certeza que deseja excluir esta faixa?')) return;

    try {
      await profitCalculatorService?.deleteIrrfBracket(id);
      await loadData();
      setSuccess('Faixa excluída com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir faixa: ' + err?.message);
      console.error('Erro ao excluir:', err);
    }
  };

  const handleSaveBracket = async (bracketData) => {
    try {
      if (editingBracket) {
        await profitCalculatorService?.updateIrrfBracket(editingBracket?.id, bracketData);
      } else {
        await profitCalculatorService?.createIrrfBracket(bracketData);
      }
      
      await loadData();
      setShowBracketModal(false);
      setSuccess(`Faixa ${editingBracket ? 'atualizada' : 'criada'} com sucesso!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar faixa: ' + err?.message);
      console.error('Erro ao salvar faixa:', err);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader userName={profile?.fullName || profile?.email} onSignOut={handleSignOut} />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Settings className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Configurações de Impostos</h1>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            Gerencie taxas padrão e tabela IRRF para cálculos de lucro
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
            <Settings className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-success">{success}</p>
          </div>
        )}

        {/* Default Tax Settings */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Valores Padrão</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Input
              label="DAS - Percentual Padrão (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={taxSettings?.defaultDasPercentual}
              onChange={(e) => setTaxSettings(prev => ({
                ...prev,
                defaultDasPercentual: e?.target?.value
              }))}
              description="Alíquota padrão do Simples"
            />
            
            <Input
              label="Pró-labore Mensal Padrão (R$)"
              type="number"
              step="0.01"
              min="0"
              value={taxSettings?.defaultProlaboreMensal}
              onChange={(e) => setTaxSettings(prev => ({
                ...prev,
                defaultProlaboreMensal: e?.target?.value
              }))}
              description="Valor padrão do pró-labore"
            />
            
            <Input
              label="INSS - Percentual Padrão (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={taxSettings?.defaultInssPercentual}
              onChange={(e) => setTaxSettings(prev => ({
                ...prev,
                defaultInssPercentual: e?.target?.value
              }))}
              description="Alíquota padrão do INSS"
            />
          </div>
          
          <Button
            onClick={handleSaveSettings}
            loading={saving}
            disabled={saving}
          >
            <Save className="h-4 w-4 mr-2" />
            Salvar Configurações
          </Button>
        </div>

        {/* IRRF Tax Brackets */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Tabela IRRF</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Faixas de imposto de renda retido na fonte
              </p>
            </div>
            <Button onClick={handleAddBracket}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Faixa
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Ordem</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Limite Superior (R$)</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Alíquota (%)</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Parcela a Deduzir (R$)</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {irrfBrackets?.map((bracket) => (
                  <IrrfBracketRow
                    key={bracket?.id}
                    bracket={bracket}
                    onEdit={handleEditBracket}
                    onDelete={handleDeleteBracket}
                  />
                ))}
                {irrfBrackets?.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-muted-foreground">
                      Nenhuma faixa cadastrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showBracketModal && (
        <IrrfBracketModal
          bracket={editingBracket}
          onSave={handleSaveBracket}
          onClose={() => setShowBracketModal(false)}
        />
      )}
    </div>
  );
};

export default TaxConfigurationSettings;